/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { DeviceModel } from '../device';
import { BrandingUiInfoKeys } from '../entities/BrandingUiInfoKeys';
import { ServicesClientImpl } from '../impls';
import { Locale } from '../locale/Locale';
import { LocaleKeys } from '../locale/LocaleKeys';
import { BrandingUiUtility } from '../utils';

export class NetworkManager {
    static SERVICE_NAME = 'NetworkServiceResponse';

    private static subscribers = {};

    private static subscribeCount = 0;

    private static mNetworkState = 0;

    private static mDebounceTime: number = 800; // in ms

    private static mDebounceTimerId: NodeJS.Timeout | null;

    // TODO: Need to update Network States values if any changes are made by samsung
    // Link: https://developer.samsung.com/smarttv/develop/api-references/samsung-product-api-references/network-api.html
    public static NetworkState = {
        INTERNET_CONNECTED: 0,
        LAN_CABLE_ATTACHED: 1,
        LAN_CABLE_DETACHED: 2,
        LAN_CABLE_STATE_UNKNOWN: 3,
        GATEWAY_CONNECTED: 4,
        GATEWAY_DISCONNECTED: 5,
        WIFI_MODULE_STATE_ATTACHED: 6,
        WIFI_MODULE_STATE_DETACHED: 7,
        WIFI_MODULE_STATE_UNKNOWN: 8,
        INTERNET_DISCONNECTED: 9,
    };

    public static onChange(state: number) {
        if (NetworkManager.mDebounceTimerId) clearTimeout(NetworkManager.mDebounceTimerId);
        NetworkManager.mDebounceTimerId = setTimeout(() => {
            const keys = Object.keys(NetworkManager.subscribers);
            this.mNetworkState = state;

            keys.forEach((key) => {
                const notify = NetworkManager.subscribers[key];
                if (typeof notify === 'function') notify(state);
            });
            NetworkManager.mDebounceTimerId = null;
        }, NetworkManager.mDebounceTime);
    }

    static subscribe(callback: Function): number {
        const ref = NetworkManager.subscribeCount;
        NetworkManager.subscribers[ref] = callback;
        NetworkManager.subscribeCount += 1;
        return ref;
    }

    static unsubscribe(ref: number) {
        delete NetworkManager.subscribers[ref];
    }

    static getNetworkState() {
        return this.mNetworkState;
    }

    static isNetworkDisconnected() {
        return (
            this.mNetworkState === NetworkManager.NetworkState.GATEWAY_DISCONNECTED ||
            this.mNetworkState === NetworkManager.NetworkState.INTERNET_DISCONNECTED
        );
    }

    static getNetworkDisconnectedMessage = () => {
        return BrandingUiUtility.formatDescription(
            Locale.getString(LocaleKeys.NO_NETWORK_WHISPER_DESCRIPTION),
            {
                DEVICE_NAME_SHORT: new ServicesClientImpl().getBrandingUiInfoForKey(
                    BrandingUiInfoKeys.DEVICE_NAME_SHORT,
                ),
                BUTTON_NAME_SELECT: DeviceModel.isWebOsPlatform()
                    ? Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_LG)
                    : Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_SAMSUNG),
            },
        );
    };
}
